import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '../../components/atoms';
import { Box, Divider, Link, Stack, styled, useTheme } from '@mui/material';
import { TableWithCustomRender } from '../../components/molecules/TableWithCustomRender';
import { useAppSelector, useIsMultiPortfolioTenant } from '../../common/hooks';
import { checkFFEnabled, formatCompactCurrency, formatCurrency, getLabelFromAccountType } from '../../utils';
import { ReactComponent as ArrowDownTrayIcon } from '../../assets/icons/arrow_down_tray.svg';
import { ReactComponent as TableCells } from '../../assets/icons/tableCells.svg';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';
import { useNavigate } from 'react-router-dom';
import { useCMSContent } from '../../common/hooks/useCmsContent';
import moment from 'moment';
import exportToExcelWithFormatter from '../../utils/ExportExcelWithFormatter';
import {
    PORTFOLIO_COMPOSITION_COLOR,
    TermTabType,
    EWS_DURATIONS,
    ASSET_CLASS_ORDER,
    STATUSVARIANTS,
    FEATURE_NAMES
} from '../../common/constants';
import { PrimaryTabs } from '../../components/molecules';
import { PortfolioPieChart } from '../../components/molecules/PortfolioPieChart';
import { DoughnutChartDataType } from '../../common/types';
import { FailedToLoadContent } from '../../components/organisms';
import { ContributionScheduleModal } from '../../components/molecules/ContributionScheduleTable/ContributionScheduleModal';
import { ContributionScheduleAccount } from '../../components/molecules/ContributionScheduleTable/ContributionScheduleTable';
import { InfoTooltip } from '../../components/molecules/InfoTooltip';
import { ReactComponent as InformationCircleIcon } from '../../assets/icons/information-circle.svg';
import { ReactComponent as InfoGoalsIcon } from '../../assets/icons/info-goals-page.svg';

const HEADER_SX = { padding: '16px 8px' };
const FIRST_HEADER_SX = { padding: '16px 8px 16px 16px' };
const PROPOSED_HEADER_SX = { padding: '16px 16px 16px 8px' };
const CELL_SX = { padding: '12px 8px' };
const FIRST_CELL_SX = { padding: '12px 8px 12px 16px' };
const PROPOSED_CELL_SX = { padding: '12px 16px 12px 8px' };

const buildPieData = (portfolioComposition: Record<string, number> = {}) => {
    const { allPortfoliosBaseColors } = PORTFOLIO_COMPOSITION_COLOR;
    const keys = Object.keys(portfolioComposition)
        .filter((key) => ASSET_CLASS_ORDER[key] !== undefined)
        .sort((a, b) => ASSET_CLASS_ORDER[a] - ASSET_CLASS_ORDER[b]);
    const doughnutChartData: DoughnutChartDataType[] = keys.map((key, index) => ({
        name: key,
        y: portfolioComposition[key],
        color: allPortfoliosBaseColors[index],
        subAssetClass: '',
        ticker: '',
        tickerLink: ''
    }));
    const groupedPortfolio = keys.map((key, index) => ({
        type: key,
        totalAllocatedvalue: `${portfolioComposition[key]}%`,
        color: allPortfoliosBaseColors[index]
    }));
    return { doughnutChartData, groupedPortfolio };
};

interface SortableHolding {
    assetCategory?: string;
    proposedHoldings?: number | string | null;
}

const sortHoldings = <T extends SortableHolding>(holdings: T[]): T[] =>
    [...holdings].sort((a, b) => {
        const aOrder = ASSET_CLASS_ORDER[a.assetCategory ?? ''] ?? 99;
        const bOrder = ASSET_CLASS_ORDER[b.assetCategory ?? ''] ?? 99;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (Number(b.proposedHoldings) || 0) - (Number(a.proposedHoldings) || 0);
    });

// Create a "current month" entry for the ContributionScheduleTable, which is prepended to the scheduled months.
const buildCurrentMonthEntry = (schedule: any, subItems: any[] = []) => {
    const date = moment(schedule?.date, 'DD-MM-YYYY').subtract(1, 'month').format('DD-MM-YYYY');
    const fundLevelHoldings = (subItems ?? []).filter((s: any) => !s?.onlyPDF);
    const totalAmt = Number((subItems ?? []).find((s: any) => s?.onlyPDF)?.proposedHoldings ?? 0);
    return { date, fundLevelHoldings, totalAmt };
};

// Transform the rebalancer per-account fundLevelHoldingsSchedule into the ContributionScheduleTable
// shape: one row per fund with a value for every scheduled month.
const buildHoldingsScheduleAccounts = (schedule: any[] = [], nowEntry: any, t: any): ContributionScheduleAccount[] => {
    if (!schedule?.length) return [];
    // Union of funds across all scheduled months so a fund present only in a later month is not dropped.
    const fundKey = (fund: any) => `${fund?.ticker ?? ''}|${fund?.assetCategory ?? ''}|${fund?.fundName ?? ''}`;
    const seenFunds = new Set<string>();
    const baseFunds = schedule
        .flatMap((entry: any) => entry?.fundLevelHoldings ?? [])
        .filter((fund: any) => {
            const key = fundKey(fund);
            if (seenFunds.has(key)) return false;
            seenFunds.add(key);
            return true;
        });
    // Prepend the current month as the leading column.
    const columns = [...schedule];
    if (nowEntry) columns.unshift(nowEntry);
    return baseFunds.map((fund: any) => ({
        name: fund?.fundName,
        subLabel: `${fund?.ticker || '-'} | ${fund?.assetCategory ? t(String(fund.assetCategory).toUpperCase()) : '-'}`,
        dateValueList: columns.map((entry: any) => {
            const match = (entry?.fundLevelHoldings ?? []).find(
                (h: any) => h?.ticker === fund?.ticker && h?.assetCategory === fund?.assetCategory
            );
            return { date: entry?.date, value: Number(match?.proposedHoldings ?? 0) };
        })
    }));
};

// Per-column Total row values, sourced from each scheduled month's totalAmt (plus the current month).
const buildHoldingsScheduleTotals = (schedule: any[] = [], nowEntry: any): { date: string; value: number }[] => {
    const columns = [...(schedule ?? [])];
    if (nowEntry) columns.unshift(nowEntry);
    return columns.map((entry: any) => ({ date: entry?.date, value: Number(entry?.totalAmt ?? 0) }));
};

const AccountScheduleCta = ({
    accountName,
    accounts,
    totalRowValues
}: {
    accountName: string;
    accounts: ContributionScheduleAccount[];
    totalRowValues: { date: string; value: number }[];
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    const isViewScheduleDisabled = !accounts || accounts.length === 0;

    const renderFundNameCell = (row: any) => {
        const subParts = row?.subLabel ? String(row.subLabel).split(' | ') : [];
        return (
            <Stack gap={0.5}>
                <Typography
                    variant="body1"
                    color="semantic.text.default"
                    label={row?.name}
                    sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '19px', wordBreak: 'break-word' }}
                />
                {subParts.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography
                            variant="labels.small"
                            color="semantic.text.muted"
                            label={subParts[0]}
                            sx={{ fontSize: '12px', fontWeight: 400, lineHeight: '17px', whiteSpace: 'nowrap' }}
                        />
                        {subParts[1] && (
                            <>
                                <Box
                                    sx={{
                                        width: '1px',
                                        height: '17px',
                                        backgroundColor: theme.palette.semantic.bd.subtle
                                    }}
                                />
                                <Typography
                                    variant="labels.small"
                                    color="semantic.text.muted"
                                    label={subParts[1]}
                                    sx={{ fontSize: '12px', fontWeight: 400, lineHeight: '17px', whiteSpace: 'nowrap' }}
                                />
                            </>
                        )}
                    </Box>
                )}
            </Stack>
        );
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isViewScheduleDisabled ? 'space-between' : 'flex-end',
                    gap: '8px',
                    marginTop: 'auto',
                    paddingTop: '16px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingBottom: '16px'
                }}
            >
                {isViewScheduleDisabled && (
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingBlock: '3px',
                                flexShrink: 0
                            }}
                        >
                            <InformationCircleIcon width={16} height={16} />
                        </Box>
                        <Box
                            component="p"
                            sx={{
                                margin: 0,
                                fontFamily: '"TT Commons Pro"',
                                fontStyle: 'italic',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '22px',
                                color: theme.palette.semantic.text.default
                            }}
                        >
                            {t('CONTRIBUTION_SCHEDULE_NOT_AVAILABLE')}
                        </Box>
                    </Box>
                )}
                <InfoTooltip message={t('VIEW_FULL_SCHEDULE_TOOLTIP')} placement="bottom-end">
                    <Box
                        component="button"
                        type="button"
                        disabled={isViewScheduleDisabled}
                        onClick={() => setIsScheduleOpen(true)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            minHeight: '24px',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: isViewScheduleDisabled ? 'default' : 'pointer',
                            color: isViewScheduleDisabled
                                ? theme.palette.neutral[300]
                                : theme.palette.semantic.int.selected,
                            '& svg path': {
                                fill: isViewScheduleDisabled
                                    ? theme.palette.neutral[300]
                                    : theme.palette.semantic.int.selected
                            }
                        }}
                    >
                        <TableCells width={16} height={16} />
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '19px',
                                textDecoration: 'underline'
                            }}
                        >
                            {t('VIEW_SCHEDULE')}
                        </Box>
                    </Box>
                </InfoTooltip>
            </Box>
            <ContributionScheduleModal
                open={isScheduleOpen}
                onClose={() => setIsScheduleOpen(false)}
                hideGoalMeta
                subtitle={accountName}
                accounts={accounts}
                firstColumnLabel={t('FUND_NAME')}
                firstColumnWidth="240px"
                showNowColumn
                showTotalRow
                totalRowValues={totalRowValues}
                trailingEmptyStateLabel={t('NO_CONTRIBUTIONS_TO_ACCOUNT')}
                horizontalScroll
                stickyFirstColumn
                maxHeight="100%"
                bodyRowHeight="56px"
                renderNameCell={renderFundNameCell}
            />
        </>
    );
};

const StyledLink = styled(Link)<{ $isEnabled?: boolean }>(({ theme, $isEnabled }) => ({
    padding: '8px 12px',
    borderRadius: '999px',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '20px',
    textUnderlinePosition: 'from-font',
    '& svg': {
        width: 20,
        height: 20
    },
    '& path': {
        fill: $isEnabled ? theme.palette.semantic.int.selected : theme.palette.neutral[300]
    },
    ...(!$isEnabled && {
        cursor: 'default',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'none'
        }
    })
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.semantic.text.muted,
    fontSize: '12px',
    lineHeight: 1.5
}));

const PlanExecutionStep = ({ retryPlanExecution }: { retryPlanExecution: () => void }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const planExecutionResponse = useAppSelector((state) => state.adviceGoals.global.planExecutionResponse);
    const planExecutionStatus = useAppSelector((state) => state.adviceGoals.global.planExecutionStatus);
    const [selectedTerm, setSelectedTerm] = useState<TermTabType>(TermTabType.SHORT);
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();
    const adviceGoalsGlobal = useAppSelector((state) => state.adviceGoals?.global);
    const auth = useAppSelector((state) => state.auth);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const dateFormat = useAppSelector((state) => state.global.globalConfig.dateFormat) || 'MM/DD/YYYY';
    const language = i18n.language;
    const orgInfo = useAppSelector((state) => state.global?.orgInfo);
    const investmentAccountType = orgInfo?.investmentAccountType?.[language] || [];
    const cmsUtil = useCMSContent();

    // Plan execution (Rebalancer) is ready only when the async call succeeded and returned a
    // usable rebalancerResult; any other state (loading, error, empty) shows the error/retry view.
    const rebalancerResult = planExecutionResponse?.rebalancerResult as Record<string, any> | undefined;
    const hasRebalancerResult = !!rebalancerResult && Object.keys(rebalancerResult).length > 0;
    const isPlanExecutionReady = planExecutionStatus === 'success' && hasRebalancerResult;
    const isFullScheduleEnabled = checkFFEnabled(FEATURE_NAMES['PLANEXECUTION_FULLSCHEDULE_FE_AEX-3226']);
    const isAmountFormatCompactFF = checkFFEnabled(FEATURE_NAMES['AMOUNT_FORMAT_COMPACT_FE_AEX-3635']);
    // Single flag that drives the Trade Instructions text color, icon fill, cursor and click guard.
    const isTradeInstructionsDisabled =
        !isPlanExecutionReady ||
        !adviceGoalsGlobal?.enablePDFGenerate ||
        adviceGoalsGlobal?.status !== STATUSVARIANTS.COMPLETE;

    useEffect(() => {
        if (location.pathname !== ADVISOR_ROUTES.PLAN_EXECUTION)
            navigate(ADVISOR_ROUTES.PLAN_EXECUTION, { replace: true });
    }, []);

    // Derive the table data from the rebalancer result. Key the memo on a STABLE CONTENT
    // SIGNATURE (a string) rather than an object reference. Save Plan dispatches syncAdviceGoals,
    // which spreads/replaces the adviceGoals + global objects - so planExecutionResponse (and its
    // rebalancerResult) can get a brand-new object reference even though the rebalancer data is
    // unchanged. Keying on a string that only changes when the data actually changes keeps
    // sourceData referentially stable, which in turn keeps pieDataByAccount -> pieCharts stable so
    // React bails out of re-rendering the pie charts on every parent Save/button click.
    const rebalancerRefKey = useMemo(() => {
        const ref = planExecutionResponse?.rebalancer_ref_id;
        if (typeof ref === 'string' && ref) return ref;
        return rebalancerResult ? JSON.stringify(rebalancerResult) : '';
    }, [planExecutionResponse?.rebalancer_ref_id, rebalancerResult]);

    const sourceData = useMemo<any[]>(() => {
        if (!hasRebalancerResult || !rebalancerResult) return [];
        return Object.keys(rebalancerResult).map((key) => ({
            accountHolder:
                rebalancerResult[key]?.accountHolder == 'primary'
                    ? 'Client'
                    : rebalancerResult[key]?.accountHolder === 'joint'
                    ? 'Joint'
                    : 'Co-Client',
            accountName: rebalancerResult[key]?.accountName,
            accountType: rebalancerResult[key]?.accountType,
            duration: rebalancerResult[key]?.duration,
            portfolioComposition: rebalancerResult[key]?.['portfolioComposition%'] || {},
            fundLevelHoldingsSchedule: rebalancerResult[key]?.fundLevelHoldingsSchedule || [],
            subItems: [
                ...sortHoldings(rebalancerResult[key]?.fundLevelHoldings || []),
                {
                    fundName: t('LABELS_TOTAL'),
                    ticker: t('LABELS_TOTAL'),
                    onlyPDF: true,
                    assetCategory: 'LABELS_TOTAL',
                    'total%': 100,
                    proposedHoldings: rebalancerResult[key]?.totalAmt
                }
            ]
        }));
    }, [rebalancerRefKey]);

    const shortCount = useMemo(
        () => sourceData.filter((item) => item.duration === EWS_DURATIONS[TermTabType.SHORT]).length,
        [sourceData]
    );
    const mediumCount = useMemo(
        () => sourceData.filter((item) => item.duration === EWS_DURATIONS[TermTabType.MEDIUM]).length,
        [sourceData]
    );
    const longCount = useMemo(
        () => sourceData.filter((item) => item.duration === EWS_DURATIONS[TermTabType.LONG]).length,
        [sourceData]
    );

    const filteredSourceData = useMemo(
        () =>
            isMultiPortfolioTenant
                ? sourceData.filter((item) => item.duration === EWS_DURATIONS[selectedTerm])
                : sourceData,
        [sourceData, selectedTerm, isMultiPortfolioTenant]
    );

    // Auto-select the first term that actually has data, but only when the current selection is
    // empty so a user's explicit tab choice is preserved. Runs only when sourceData changes (rare).
    useEffect(() => {
        if (!isMultiPortfolioTenant || sourceData.length === 0) return;
        const counts = {
            [TermTabType.SHORT]: shortCount,
            [TermTabType.MEDIUM]: mediumCount,
            [TermTabType.LONG]: longCount
        };
        if (counts[selectedTerm] > 0) return;
        if (shortCount > 0) setSelectedTerm(TermTabType.SHORT);
        else if (mediumCount > 0) setSelectedTerm(TermTabType.MEDIUM);
        else if (longCount > 0) setSelectedTerm(TermTabType.LONG);
    }, [sourceData, shortCount, mediumCount, longCount, isMultiPortfolioTenant]);

    // Precompute pie data once per account, then memoize the chart ELEMENTS. Handing React the same
    // element reference across renders lets it bail out of re-rendering PortfolioPieChart when the
    // data hasn't changed - without wrapping the component in React.memo (which broke other call
    // sites by changing its export type).
    const pieDataByAccount = useMemo(
        () => filteredSourceData.map((item) => buildPieData(item.portfolioComposition)),
        [filteredSourceData]
    );
    const pieCharts = useMemo(
        () =>
            pieDataByAccount.map(({ doughnutChartData, groupedPortfolio }, index) => (
                <PortfolioPieChart
                    key={index}
                    doughnutChartData={doughnutChartData}
                    groupedPortfolio={groupedPortfolio}
                    legendPosition="bottom"
                    showSeparators={false}
                    size={200}
                />
            )),
        [pieDataByAccount]
    );

    const accountTableHeaders = useMemo(
        () => [
            {
                label: t('FUND_NAME'),
                key: 'fundName',
                align: 'left' as const,
                width: '336px',
                headerSx: FIRST_HEADER_SX,
                cellSx: FIRST_CELL_SX,
                render: (value: any, row: any) =>
                    row?.ticker === t('LABELS_TOTAL') ? (
                        <Typography
                            variant="body1"
                            label={t('TOTAL')}
                            sx={{ color: theme.palette.semantic.text.default, fontWeight: 600 }}
                        />
                    ) : (
                        <Typography variant="body1" label={value} sx={{ color: theme.palette.semantic.text.default }} />
                    )
            },
            {
                label: t('TICKER'),
                key: 'ticker',
                align: 'left' as const,
                width: '104px',
                headerSx: HEADER_SX,
                cellSx: CELL_SX,
                render: (value: any, row: any) =>
                    row?.ticker === t('LABELS_TOTAL') ? null : (
                        <Typography variant="body1" label={value} sx={{ color: theme.palette.semantic.text.default }} />
                    )
            },
            {
                label: t('ASSET_CATEGORY'),
                key: 'assetCategory',
                align: 'left' as const,
                width: '120px',
                headerSx: HEADER_SX,
                cellSx: CELL_SX,
                render: (value: any, row: any) =>
                    row?.ticker === t('LABELS_TOTAL') ? null : (
                        <Typography
                            variant="body1"
                            label={value ? t(value.toUpperCase()) : ''}
                            sx={{ color: theme.palette.semantic.text.default }}
                        />
                    )
            },
            {
                label: t('PROPOSED_HOLDINGS'),
                key: 'proposedHoldings',
                align: 'right' as const,
                width: '176px',
                headerSx: PROPOSED_HEADER_SX,
                cellSx: PROPOSED_CELL_SX,
                headerRender: () => (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {t('PROPOSED_HOLDINGS')}
                        <InfoTooltip message={t('PROPOSED_HOLDINGS_INFO')} icon={InfoGoalsIcon} />
                    </Box>
                ),
                render: (value: any, row: any) => (
                    <Typography
                        variant="body1"
                        label={isAmountFormatCompactFF ? formatCompactCurrency(value) : formatCurrency(value)}
                        sx={{
                            color: theme.palette.semantic.text.default,
                            fontWeight: row?.ticker === t('LABELS_TOTAL') ? 600 : 'normal'
                        }}
                    />
                )
            },
            {
                label: t('PERCENT_OF_TOTAL'),
                key: 'total%',
                align: 'left' as const,
                width: '104px',
                headerSx: HEADER_SX,
                cellSx: CELL_SX,
                render: (value: any, row: any) => (
                    <Typography
                        variant="body1"
                        label={value != null ? `${value}%` : ''}
                        sx={{
                            color: theme.palette.semantic.text.default,
                            fontWeight: row?.ticker === t('LABELS_TOTAL') ? 600 : 'normal'
                        }}
                    />
                )
            }
        ],
        [t, theme]
    );

    // Memoize the whole account-cards list (card + pie + table per account) so that when this
    // component re-renders for an unrelated reason (parent/loader cascade) but the underlying data
    // is unchanged, React receives the same element array and bails out of re-rendering every card
    // and its Highcharts pie. It only recomputes when the data (filteredSourceData/pieCharts) or
    // the memoized headers actually change. Key is derived from the account identity (not
    // selectedTerm) so a stable selection never forces a remount.
    const accountCards = useMemo(
        () =>
            filteredSourceData.map((item, index) => (
                <Box
                    key={`${item.accountName ?? ''}-${item.duration ?? ''}-${index}`}
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'stretch',
                        border: `1px solid ${theme.palette.semantic.bd.default}`,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: theme.palette.common.white
                    }}
                >
                    <Box
                        sx={{
                            width: '250px',
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            padding: '16px',
                            borderRight: `1px solid ${theme.palette.semantic.bd.default}`
                        }}
                    >
                        <Stack gap={0.5}>
                            <Typography
                                variant="headers.h4"
                                label={item.accountName}
                                sx={{ color: theme.palette.semantic.text.default }}
                            />
                            <Typography
                                variant="labels.small"
                                label={t(item.accountHolder.toUpperCase())}
                                sx={{ color: theme.palette.semantic.text.muted }}
                            />
                        </Stack>
                        <Divider />
                        {pieCharts[index]}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'clip', display: 'flex', flexDirection: 'column' }}>
                        <TableWithCustomRender
                            headers={accountTableHeaders}
                            dataSource={item.subItems || []}
                            rowHeight="51px"
                            bodyRowHeight="46px"
                            variant="ftds"
                            embedded
                        />
                        {isFullScheduleEnabled &&
                            (() => {
                                const schedule = item.fundLevelHoldingsSchedule ?? [];
                                const nowEntry = buildCurrentMonthEntry(schedule[0], item.subItems ?? []);
                                const scheduleAccounts = buildHoldingsScheduleAccounts(schedule, nowEntry, t);
                                return (
                                    <AccountScheduleCta
                                        accountName={item.accountName}
                                        accounts={scheduleAccounts}
                                        totalRowValues={buildHoldingsScheduleTotals(schedule, nowEntry)}
                                    />
                                );
                            })()}
                    </Box>
                </Box>
            )),
        [filteredSourceData, pieCharts, accountTableHeaders, theme, t, isFullScheduleEnabled]
    );

    const handleTradeInstructions = () => {
        if (isTradeInstructionsDisabled) return;
        const cms_planExecutionTradeReportDisclaimer = cmsUtil.getContentAsString(
            'plan_execution.trade_report.disclaimer'
        );
        const globalData: any = adviceGoalsGlobal;
        const currencyCode = formatCurrency(1, true).replace(/1/, '').trim();
        const flattenedData = sourceData.flatMap((item) =>
            item.subItems
                .filter((subItem: any) => subItem.fundName !== ' ')
                .map((subItem: any) => ({
                    asOfDate: moment(globalData?.lastUpdateDate).format(dateFormat),
                    advisorEmail: auth?.user?.userEmail,
                    // Investor ID = the Customer ID entered on the profile (saved as participant_id).
                    investorId: (adviceClient?.participant_id as string) ?? '',
                    accountName: item.accountName,
                    owner: t(item.accountHolder.toUpperCase()),
                    accountType: getLabelFromAccountType(item.accountType, investmentAccountType),
                    duration: isMultiPortfolioTenant ? t(item.duration.split(' ')[0].toUpperCase()) : '',
                    assetCategory: t(subItem.assetCategory.toUpperCase()) || '',
                    ticker: subItem.ticker || '',
                    fundName: subItem.fundName || '',
                    'total%': subItem['total%'] || 0,
                    targetAllocation: subItem.proposedHoldings || 0,
                    currencyCode
                }))
        );
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        exportToExcelWithFormatter(
            //AS_OF_DATE	ADVISOR_EMAIL	INVESTOR_ID	ACCOUNT_NAME	OWNER	ACCOUNT_TYPE	DURATION	Type	TICKER	FUND_NAME	%_OF_TOTAL	TARGET_ALLOCATION	CURRENCY_CODE
            flattenedData,
            [
                { header: t('AS_OF_DATE'), value: (row) => row.asOfDate, width: 20 },
                { header: t('ADVISOR_EMAIL'), value: (row) => row.advisorEmail, width: 40 },
                { header: t('INVESTOR_ID'), value: (row) => row.investorId, width: 20 },
                { header: t('ACCOUNT_NAME'), value: (row) => row.accountName, width: 30 },
                { header: t('COLUMN_OWNER'), value: (row) => row.owner, width: 20 },
                { header: t('ACCOUNT_TYPE'), value: (row) => row.accountType, width: 20 },
                ...(isMultiPortfolioTenant
                    ? [{ header: t('COLUMN_DURATION'), value: (row: any) => row.duration, width: 20 }]
                    : []),
                { header: t('ASSET_CATEGORY_EXCEL_LABEL'), value: (row) => row.assetCategory, width: 20 },
                { header: t('COLUMN_TICKER'), value: (row) => row.ticker, width: 20 },
                { header: t('COLUMN_FUND_NAME'), value: (row) => row.fundName, width: 40 },
                { header: t('%_OF_TOTAL'), value: (row) => row['total%'], width: 20 },
                { header: t('TARGET_ALLOCATION'), value: (row) => row.targetAllocation, width: 20 },
                { header: t('CURRENCY_CODE'), value: (row) => row.currencyCode, width: 20 }
            ],
            `${t('TEXT_TRADE_REPORT')}_${yyyy}${mm}${dd}.xlsx`,
            t('TEXT_TRADE_REPORT'),
            true,
            [{ text: cms_planExecutionTradeReportDisclaimer, column: t('TEXT_TRADE_DISCLOURES') + ':' }],
            t('TEXT_TRADE_DISCLOURES')
        );
    };
    return (
        <>
            <Box>
                {!isPlanExecutionReady ? (
                    <Box
                        sx={{
                            mt: 2,
                            p: 3
                        }}
                    >
                        {planExecutionStatus === 'error' && <FailedToLoadContent onRetry={retryPlanExecution} />}
                        {planExecutionStatus !== 'error' && <div style={{ minHeight: '60vh' }} />}
                    </Box>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        <Box
                            sx={{
                                position: 'relative',
                                ...(!isMultiPortfolioTenant && { display: 'flex', justifyContent: 'flex-end' })
                            }}
                        >
                            {isMultiPortfolioTenant && (
                                <PrimaryTabs
                                    ftdsBadge
                                    value={selectedTerm}
                                    onChange={(_e, val) => setSelectedTerm(val)}
                                    tabItems={[
                                        {
                                            key: TermTabType.SHORT,
                                            label: t('SHORT'),
                                            count: shortCount,
                                            disabled: shortCount === 0,
                                            tooltipKey: shortCount === 0 ? 'TERM_EMPTY_TOOLTIP' : undefined
                                        },
                                        {
                                            key: TermTabType.MEDIUM,
                                            label: t('MEDIUM'),
                                            count: mediumCount,
                                            disabled: mediumCount === 0,
                                            tooltipKey: mediumCount === 0 ? 'TERM_EMPTY_TOOLTIP' : undefined
                                        },
                                        {
                                            key: TermTabType.LONG,
                                            label: t('LONG'),
                                            count: longCount,
                                            disabled: longCount === 0,
                                            tooltipKey: longCount === 0 ? 'TERM_EMPTY_TOOLTIP' : undefined
                                        }
                                    ]}
                                />
                            )}
                            <StyledLink
                                variant="body2"
                                underline="always"
                                color={
                                    isTradeInstructionsDisabled
                                        ? theme.palette.neutral[300]
                                        : theme.palette.semantic.text.default
                                }
                                theme={theme}
                                $isEnabled={!isTradeInstructionsDisabled}
                                onClick={handleTradeInstructions}
                                aria-disabled={isTradeInstructionsDisabled}
                                sx={{
                                    ...(isMultiPortfolioTenant && { position: 'absolute', top: '18px', right: 0 }),
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: isTradeInstructionsDisabled ? 'default' : 'pointer'
                                }}
                            >
                                <ArrowDownTrayIcon />
                                {t('TEXT_TRADE_INSTRUCTIONS')}
                            </StyledLink>
                        </Box>
                        <Stack spacing={2} sx={{ py: 2 }}>
                            {accountCards}
                            <Box>
                                <StyledTypography
                                    variant="body.small"
                                    label={cmsUtil.getContentAsString('plan_execution.trade_report_table.disclaimer')}
                                />
                            </Box>
                        </Stack>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default PlanExecutionStep;
